import React, { Component, ReactNode, ErrorInfo } from "react";
import * as Sentry from "@sentry/react";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

class RootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to Sentry
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack
      },
      tags: {
        errorType: error.name,
        errorSource: 'RootErrorBoundary'
      },
      level: 'fatal' // Since this is a root-level error
    });

    this.setState({
      errorInfo
    });

    // You might want to log to your analytics service here
    // analytics.trackError('fatal_error', { error, errorInfo });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="mb-6">
              <div className="text-xl font-bold mb-2">
                Oops! Something went wrong
              </div>
              <div>
                <div className="mt-2 text-sm">
                  {process.env.NODE_ENV === 'development' ? (
                    <>
                      <div className="mb-4 p-4 bg-red-50 rounded-md">
                        <p className="font-mono text-sm text-red-800">
                          {this.state.error?.message}
                        </p>
                        {this.state.errorInfo && (
                          <pre className="mt-2 whitespace-pre-wrap text-xs text-red-700">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="mb-4">
                      We've encountered an unexpected error. Our team has been notified and is working to fix it.
                    </p>
                  )}
                  <div className="flex gap-4">
                    <button
                      onClick={this.handleRefresh}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors"
                    >
                      Refresh Page
                    </button>
                    <button
                      onClick={this.handleGoHome}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
                    >
                      Go to Homepage
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;